import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { reviewQuestions, toolChoices } from '../data/llmVsSkp';
import { easeOutStrong, fadeUp } from '../utils/motion';
const toolStyles: Record<string, string> = {
  llm: 'bg-surface-muted text-ink-dark',
  skp: 'bg-navy text-white',
  both: 'bg-teal text-white'
};
export function LlmVsSkpPage() {
  return <>
      {/* Hero */}
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-2999" data-mp="1kjf7bmqcf8kx">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3000" data-mp="1pd1a8n6pfy3j">
          <a href="/semantic-knowledge" className="inline-flex items-center gap-2 text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150 mb-8" data-id="element-3001" data-mp="1fjas00168jwa">
            <ArrowLeft size={16} data-id="element-3002" data-mp="31cvpgxgqortl" />
            Semantic Knowledge Platform
          </a>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3003" data-mp="31eqeonz1n63h">
            <div className="lg:col-span-5" data-id="element-3004" data-mp="2wdj5qll600v8">
              <p className="text-sm font-medium text-teal mb-5" data-id="element-3005" data-mp="3ae3nrfj99sno">LLM vs. SKP</p>
              <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-3006" data-mp="35ehbb8b94gfx">
                What’s the difference?
              </h1>
            </div>
            <div className="lg:col-span-7 lg:pt-10" data-id="element-3007" data-mp="2dnb6iekl5ukb">
              <p className="text-2xl text-ink-dark leading-relaxed" data-id="element-3008" data-mp="2dom765iqim77">
                An LLM is good at reading, writing, summarizing, and suggesting
                connections. SyNeural’s Semantic Knowledge Platform (SKP) is
                designed to manage the knowledge an organization has chosen to
                review, approve, and use.
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3009" data-mp="1tykb2nxyqa3e">
            <div className="rounded-lg border border-line bg-surface-bg p-8" data-id="element-3010" data-mp="3u237g3ie1wd5">
              <p className="text-sm font-medium text-ink-muted mb-3" data-id="element-3011" data-mp="2va711vf619o">Think of the LLM as</p>
              <p className="font-heading font-bold text-2xl text-ink-dark mb-3" data-id="element-3012" data-mp="7m0eckneoe70">
                A capable research assistant
              </p>
              <p className="text-ink-muted leading-relaxed" data-id="element-3013" data-mp="ck1xt6oqufj0">
                It reads, writes, summarizes, and suggests.
              </p>
            </div>
            <div className="rounded-lg bg-navy text-white p-8" data-id="element-3014" data-mp="3g1sjp9cdkxw7">
              <p className="text-sm font-medium text-white/70 mb-3" data-id="element-3015" data-mp="3g229owxf751w">Think of SKP as</p>
              <p className="font-heading font-bold text-2xl mb-3" data-id="element-3016" data-mp="3l03ov6rq6k83">
                The organization’s working knowledge system
              </p>
              <p className="text-white/80 leading-relaxed" data-id="element-3017" data-mp="10ao4atj3nrqw">
                Its sources, definitions, connected ideas, review decisions, and
                history.
              </p>
            </div>
          </div>

          <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-3xl" data-id="element-3018" data-mp="2outorqmizv6d">
            <a href="/noetivra" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy transition-colors duration-150" data-id="element-3019" data-mp="2ksov4rw2uyjq">
              Noetivra
            </a>{' '}
            provides the interactive workspace through which people explore SKP
            knowledge and review AI-assisted suggestions.
          </p>
        </motion.div>
      </section>

      {/* Why not just train */}
      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-3020" data-mp="214g3ciyg4ukr">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3021" data-mp="2172aihhfd82g">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-3022" data-mp="1s5m0wbyfp9za">
            Why can’t you just train an LLM?
          </h2>
          <div className="lg:col-span-7 space-y-5" data-id="element-3023" data-mp="3mj3p816nyavd">
            <p className="text-xl text-ink-dark leading-relaxed" data-id="element-3024" data-mp="2j7539ynqxoif">
              Training helps an LLM learn patterns. But you cannot simply open
              its learned knowledge, inspect every claim, change one approved
              definition, or remove the influence of a retracted study from its
              model weights.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-3025" data-mp="2f48etrbiyy6k">
              SKP keeps important knowledge outside the model, where people can
              examine and update it. The LLM can read from that knowledge and
              suggest additions. Approved reviewers and rules determine what
              becomes part of it.
            </p>
          </div>
        </motion.div>
      </section>

      {/* How they work together */}
      <section className="bg-white py-20 lg:py-28" data-id="element-3026" data-mp="1vf7rg7kaqwhb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3027" data-mp="1hiub51ri114f">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14" data-id="element-3028" data-mp="1mgmbmk67lvk8">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-3029" data-mp="12uucjdeqy4d9">
              How they work together
            </h2>
            <p className="lg:col-span-7 text-xl text-ink-dark leading-relaxed lg:pt-3" data-id="element-3030" data-mp="1cta2ve5y99lp">
              Imagine an LLM reads a scientific paper. It proposes that a
              particular intervention was studied for a condition in a defined
              population and identifies the passage describing the outcome.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch" data-id="element-3031" data-mp="1guc39yblrrp9">
            <div className="lg:col-span-4 rounded-lg border border-line bg-surface-bg p-6 lg:p-8 flex flex-col" data-id="element-3032" data-mp="1vj88lnm9yqm6">
              <p className="text-sm font-medium text-ink-muted mb-3" data-id="element-3033" data-mp="1qlgecmy5qset">The LLM proposes</p>
              <ul className="space-y-3 text-ink-dark" data-id="element-3034" data-mp="24kzfuwwgr5i5">
                <li data-id="element-3035" data-mp="20je3dcnuct8h">An intervention</li>
                <li data-id="element-3036" data-mp="20kfh5ow8rttw">A condition</li>
                <li data-id="element-3037" data-mp="27m2jan1qvz0h">A defined population</li>
                <li data-id="element-3038" data-mp="27n3xh0e251mf">The passage describing the outcome</li>
              </ul>
              <p className="mt-auto pt-6 text-sm text-ink-muted" data-id="element-3039" data-mp="2ckm60po5xetw">A proposal, not yet knowledge.</p>
            </div>

            <div className="lg:col-span-8 rounded-lg bg-navy text-white p-6 lg:p-8" data-id="element-3040" data-mp="2hj75unzi5wht">
              <p className="text-sm font-medium text-white/70 mb-4" data-id="element-3041" data-mp="2mb70c0wszx70">SKP then helps answer</p>
              <ol className="space-y-4" data-id="element-3042" data-mp="2qd21n5eqiaks">
                {reviewQuestions.map(question => <li key={question} className="flex gap-3" data-id="element-3043" data-mp="2vb3u2gy1vm8x">
                    <Check size={20} className="text-teal shrink-0 mt-0.5" aria-hidden="true" data-id="element-3044" data-mp="2vdq18fh13zqm" />
                    <span className="text-lg leading-snug" data-id="element-3045" data-mp="308n75dj0hsza">{question}</span>
                  </li>)}
              </ol>
            </div>
          </motion.div>

          <motion.p {...fadeUp} className="mt-10 text-lg text-ink-muted leading-relaxed max-w-3xl lg:ml-auto lg:max-w-none lg:w-2/3" data-id="element-3046" data-mp="15y0b2201fh76">
            Once approved, the connection becomes part of a knowledge graph that
            researchers can search and explore. If the paper is later corrected,
            the affected connections can be found and reviewed.
          </motion.p>
        </div>
      </section>

      {/* NCJI */}
      <section className="bg-surface-bg py-20 lg:py-28 border-y border-line" data-id="element-3047" data-mp="3fbn7e2vfsdtc">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3048" data-mp="3ag7wnjsq6y15">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-3049" data-mp="6xoi1k2yzqot">
            Why this matters for NCJI
          </h2>
          <div className="lg:col-span-7 space-y-5" data-id="element-3050" data-mp="3t5ejnj0jybsr">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-3051" data-mp="3t4wgs3jdwzbv">
              The same principle applies to SyNeural’s{' '}
              <a href="/human-capability" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy transition-colors duration-150" data-id="element-3052" data-mp="2mmpw4jpn7ha3">
                Human Capability Framework
              </a>
              . An LLM might help explain an{' '}
              <a href="/ncji" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy transition-colors duration-150" data-id="element-3053" data-mp="qjux0yb1vk8x">
                NCJI
              </a>{' '}
              assessment in everyday language. SKP would maintain the approved
              capability definitions, tasks, rubrics, research, and
              interpretation rules behind that explanation.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-3054" data-mp="uo2lcudliy6m">
              If a rule changes or someone challenges a finding, the connected
              systems need to show which version was used and allow the result
              to be reviewed or corrected.
            </p>
            <p className="font-heading font-bold text-xl text-ink-dark leading-snug border-l-4 border-teal pl-5" data-id="element-3055" data-mp="1s2wyqzs2fq76">
              The LLM can help communicate that information, but it should not
              decide a person’s standing on its own.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Which one is better */}
      <section className="bg-white py-20 lg:py-28" data-id="element-3056" data-mp="3mhu0tbmbtx89">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3057" data-mp="3hk26yc24g11f">
          <motion.div {...fadeUp} className="lg:col-span-4" data-id="element-3058" data-mp="2tty78fs20lfp">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight mb-4" data-id="element-3059" data-mp="2twkeeeb18yxe">
              Which one is better?
            </h2>
            <p className="text-xl text-ink-muted" data-id="element-3060" data-mp="32wpk8k0kvp9g">They solve different problems.</p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-8 overflow-x-auto" data-id="element-3061" data-mp="2yrzrh417uqsr">
            <table className="w-full text-left border-collapse" data-id="element-3062" data-mp="i3vruj0irvdf">
              <thead data-id="element-3063" data-mp="e3lo7d079h1q">
                <tr className="border-b-2 border-ink-dark" data-id="element-3064" data-mp="e43lmcylm1b8">
                  <th scope="col" className="py-3 pr-6 font-heading font-bold text-sm text-ink-dark" data-id="element-3065" data-mp="3uwhi5plup51m">
                    If you need to…
                  </th>
                  <th scope="col" className="py-3 font-heading font-bold text-sm text-ink-dark w-56" data-id="element-3066" data-mp="3qvfi56k40oyl">
                    Use…
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-3067" data-mp="3h4b3k7ycj64a">
                {toolChoices.map(row => <tr key={row.need} className="border-b border-line align-middle" data-id="element-3068" data-mp="3c6j0chs93xks">
                    <td className="py-4 pr-6 text-ink-dark leading-relaxed" data-id="element-3069" data-mp="3776d3vxgwoho">{row.need}</td>
                    <td className="py-4" data-id="element-3070" data-mp="335be4y30en72">
                      <span className={`inline-block rounded-md px-3 py-1.5 text-sm font-semibold whitespace-nowrap ${toolStyles[row.tool]}`} data-id="element-3071" data-mp="336cof0c7b5nq">
                        {row.use}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>

            <p className="mt-10 text-lg text-ink-muted leading-relaxed" data-id="element-3072" data-mp="wg6g5bonun58">
              If all you need is to chat with a few documents, an LLM with
              document search may be enough. SKP becomes valuable when many
              people and applications need to use the same knowledge
              consistently, see where it came from, and update it when
              understanding changes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-3073" data-mp="l0trjywy5aul">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3074" data-mp="pyd855h49zpp">
          <p className="font-heading font-bold text-3xl lg:text-4xl leading-snug tracking-tight mb-10" data-id="element-3075" data-mp="uxy2t3p97lwm">
            The LLM processes language. SKP manages the working knowledge the
            organization can inspect, correct, and build upon.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3076" data-mp="26clhfycvmcje">
            <a href="/semantic-knowledge" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3077" data-mp="2ad5aapqf2tzq">
              Explore SKP
              <ArrowRight size={18} data-id="element-3078" data-mp="k93ghipy55hq" />
            </a>
            <a href="/contact" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3079" data-mp="fba8ql8zw3fc">
              Discuss Your Use Case
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}